import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabRepondre.module.css';
import MetaHead from '../components/MetaHead';
import { normalizeTimestamp } from '../utils/normalizeTimestamp';
import { formatDate } from '../utils/format';

/* ============================================================
   TabRepondre — onglet "Répondre aux avis"
   ------------------------------------------------------------
   IDs DOM préservés pour le backend Express :
     #rr-gmb-banner   (bandeau Google Business)
     #rr-list         (conteneur de la liste)
     [data-filter]    (chips de filtre — all / pending / published)

   Comportements préservés (callbacks fournis en props) :
     - onFilterChange(value)   ← filterReplies(filter)
     - onGenerate(review)      ← genererReponseIA(reviewId)
     - onPublish(review,text)  ← publierReponse(reviewId, text)
     - onSaveDraft(r,text)     ← sauverBrouillon(reviewId, text)
   ============================================================ */

// FILTERS i18n-isés : labels résolus en runtime via `t('repondre.filter_*')`
const FILTER_KEYS = [
  { value: 'all',       labelKey: 'repondre.filter_all',       emoji: '📋' },
  { value: 'pending',   labelKey: 'repondre.filter_pending',   emoji: '⏳' },
  { value: 'published', labelKey: 'repondre.filter_published', emoji: '✓' },
];

function initialsFor(name = '') {
  return name
    .split(' ').filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}

function buildTimeAgo(t, lang) {
  return function timeAgo(value) {
    // Robuste face aux 3 formats backend : ISO string / Unix seconds / Unix ms
    const d = normalizeTimestamp(value);
    if (!d) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60)        return t('repondre.time_just_now');
    if (diff < 3600)      return t('repondre.time_minutes_ago', { count: Math.floor(diff / 60) });
    if (diff < 86400)     return t('repondre.time_hours_ago',   { count: Math.floor(diff / 3600) });
    if (diff < 86400 * 7) return t('repondre.time_days_ago',    { count: Math.floor(diff / 86400) });
    // Plus de 7 jours → date locale courte (15 mai / 15 May / 15 may / 15 mag)
    return formatDate(d, lang, { day: 'numeric', month: 'short' });
  };
}

/* ----------------------------------------------------------
   ReplyCard
---------------------------------------------------------- */
function ReplyCard({ review, onGenerate, onPublish, onSaveDraft, t, timeAgo }) {
  const initialDraft = review.draft || review.reply?.text || '';
  const [draft, setDraft] = useState(initialDraft);
  const [savedDraft, setSavedDraft] = useState(initialDraft);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const isPublished = !!review.replied;

  // Auto-save brouillon onBlur — silencieux, déclenché seulement si modification ET texte non vide
  const handleBlurDraft = useCallback(async () => {
    if (!onSaveDraft || isPublished) return;
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed === savedDraft.trim()) return;
    const ok = await onSaveDraft(review, trimmed);
    if (ok) setSavedDraft(trimmed);
  }, [onSaveDraft, isPublished, draft, savedDraft, review]);

  const handleGenerate = useCallback(async () => {
    if (!onGenerate) return;
    setGenerating(true);
    try {
      const text = await onGenerate(review);
      if (text) setDraft(text);
    } finally {
      setGenerating(false);
    }
  }, [onGenerate, review]);

  const handlePublish = useCallback(async () => {
    if (!onPublish || !draft.trim()) return;
    setPublishing(true);
    try { await onPublish(review, draft); }
    finally { setPublishing(false); }
  }, [onPublish, review, draft]);

  return (
    <article className={styles.replyCard} data-review-id={review.id}>
      <header className={styles.head}>
        <div className={styles.author}>
          <div className={styles.avatar}>{initialsFor(review.author)}</div>
          <div>
            <div className={styles.authorName}>{review.author || t('repondre.anonymous_client')}</div>
            <div className={styles.authorSub}>
              <span>{timeAgo(review.date)}</span>
              <span className={styles.dot}>·</span>
              <span className={styles.stars}>{'★'.repeat(review.rating || 0)}</span>
            </div>
          </div>
        </div>
        {isPublished ? (
          <span className={`${styles.statusPill} ${styles.statusPublished}`}>{t('repondre.status_published')}</span>
        ) : (
          <span className={`${styles.statusPill} ${styles.statusPending}`}>{t('repondre.status_pending')}</span>
        )}
      </header>

      {review.text && <p className={styles.reviewText}>"{review.text}"</p>}

      <div className={styles.draftZone}>
        <label className={styles.draftLabel}>
          {isPublished ? t('repondre.draft_label_published') : t('repondre.draft_label_pending')}
        </label>
        <textarea
          className={styles.draftArea}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleBlurDraft}
          placeholder={t('repondre.draft_placeholder')}
          disabled={isPublished}
          rows={4}
        />
        {!isPublished && (
          <div className={styles.draftActions}>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? t('repondre.btn_generating') : t('repondre.btn_generate')}
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handlePublish}
              disabled={publishing || !draft.trim()}
            >
              {publishing ? t('repondre.btn_publishing') : t('repondre.btn_publish')}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

/* ----------------------------------------------------------
   States
---------------------------------------------------------- */
function ReplySkeleton() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      <div className={styles.skHead}>
        <div className={styles.skAvatar} />
        <div className={styles.skMeta}>
          <div className={`${styles.skLine} ${styles.skLineW40}`} />
          <div className={`${styles.skLine} ${styles.skLineW70}`} />
        </div>
      </div>
      <div className={`${styles.skLine} ${styles.skLineFull}`} />
      <div className={styles.skBox} />
    </div>
  );
}

function EmptyState({ filter, t }) {
  const config = {
    all:       { emoji: '🍽️', titleKey: 'repondre.empty_all_title',       textKey: 'repondre.empty_all_text' },
    pending:   { emoji: '🎉', titleKey: 'repondre.empty_pending_title',   textKey: 'repondre.empty_pending_text' },
    published: { emoji: '📭', titleKey: 'repondre.empty_published_title', textKey: 'repondre.empty_published_text' },
  }[filter] || { emoji: '🔍', titleKey: 'repondre.empty_default_title', textKey: 'repondre.empty_default_text' };

  return (
    <div className={styles.empty}>
      <div className={styles.emptyEmoji}>{config.emoji}</div>
      <h3 className={styles.emptyTitle}>{t(config.titleKey)}</h3>
      <p className={styles.emptyText}>{t(config.textKey)}</p>
    </div>
  );
}

/* ----------------------------------------------------------
   TabRepondre
---------------------------------------------------------- */
export default function TabRepondre({
  reviews = [],
  loading = false,
  gmbConnected = false,

  // Callbacks (compatibles fonctions globales Express)
  onFilterChange,
  onGenerate,
  onPublish,
  onSaveDraft,
  onConnectGMB,

  initialFilter = 'all',
}) {
  const { t, i18n } = useTranslation('tabs');
  const lang = i18n.language || 'fr';
  const timeAgo = useMemo(() => buildTimeAgo(t, lang), [t, lang]);
  const [filter, setFilter] = useState(initialFilter);

  const handleFilter = useCallback((value) => {
    setFilter(value);
    onFilterChange && onFilterChange(value);
  }, [onFilterChange]);

  const filtered = useMemo(() => {
    if (filter === 'pending')   return reviews.filter((r) => !r.replied);
    if (filter === 'published') return reviews.filter((r) => r.replied);
    return reviews;
  }, [reviews, filter]);

  const counts = useMemo(() => ({
    all:       reviews.length,
    pending:   reviews.filter((r) => !r.replied).length,
    published: reviews.filter((r) => r.replied).length,
  }), [reviews]);

  return (
    <>
      <MetaHead page="repondre" />
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1><span className={styles.titleEmoji}>{t('repondre.title_emoji')}</span>{t('repondre.title')}</h1>
          <p>{t('repondre.subtitle')}</p>
        </div>
      </header>

      {!gmbConnected && (
        <div id="rr-gmb-banner" className={styles.banner}>
          <span className={styles.bannerIcon}>🔌</span>
          <div className={styles.bannerBody}>
            <strong>{t('repondre.banner_gmb_strong')}</strong>
            <span>{t('repondre.banner_gmb_text')}</span>
          </div>
          {onConnectGMB && (
            <button type="button" className={styles.btnPrimary} onClick={onConnectGMB}>
              {t('repondre.banner_gmb_cta')}
            </button>
          )}
        </div>
      )}

      <div className={styles.filters}>
        {FILTER_KEYS.map((f) => (
          <button
            key={f.value}
            type="button"
            data-filter={f.value}
            className={`${styles.filterChip} ${
              filter === f.value ? styles.filterChipActive : ''
            } rr-filter-btn`}
            onClick={() => handleFilter(f.value)}
          >
            <span>{f.emoji}</span>
            {t(f.labelKey)}
            <span className={styles.chipCount}>{counts[f.value] ?? 0}</span>
          </button>
        ))}
      </div>

      <div id="rr-list" className={styles.list}>
        {loading && reviews.length === 0 && (
          <>
            <ReplySkeleton />
            <ReplySkeleton />
            <ReplySkeleton />
          </>
        )}

        {!loading && filtered.length === 0 && <EmptyState filter={filter} t={t} />}

        {!loading && filtered.map((r) => (
          <ReplyCard
            key={r.id ?? r.reviewId ?? `${r.author}-${r.date}`}
            review={r}
            onGenerate={onGenerate}
            onPublish={onPublish}
            onSaveDraft={onSaveDraft}
            t={t}
            timeAgo={timeAgo}
          />
        ))}
      </div>
    </>
  );
}
